/**
 * WORKSPACE-SWITCHER-001 — post-login chooser, shown only when the user has
 * two or more workspaces and no choice exists for this browser session.
 * Same account, same JWT, same attendance session — pure navigation.
 */
import React, { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, Landmark, ClipboardList, LayoutGrid, ShieldCheck, Info } from 'lucide-react';
import TitleFlowLogo from '../components/TitleFlowLogo';
import { useAuth } from '../App';
import { usePermissions } from '../hooks/usePermissions';
import { deriveWorkspaces, getActiveWorkspace, setActiveWorkspace } from '../utils/workspaces';

const ICONS = { order_desk: ClipboardList, jurisdiction_requestor: Landmark };

const WorkspaceCard = ({ ws, index, onChoose }) => {
  const Icon = ICONS[ws.id] || LayoutGrid;
  return (
    <button
      type="button"
      onClick={() => onChoose(ws)}
      data-testid={`workspace-card-${ws.id}`}
      style={{ animationDelay: `${120 + index * 90}ms` }}
      className="group w-full text-left rounded-2xl border border-slate-200 bg-white p-5 transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-[0_18px_40px_-20px_rgba(37,99,235,0.45)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 animate-in fade-in slide-in-from-bottom-2"
    >
      <div className="flex items-start gap-4">
        <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${ws.id === 'order_desk' ? 'bg-blue-600' : 'bg-violet-600'} text-white shadow-sm`}>
          <Icon className="h-6 w-6" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-base font-semibold text-slate-900">{ws.label}</span>
            {ws.primary && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Primary role</span>}
          </span>
          <span className="mt-1 block text-sm text-slate-500 leading-snug">{ws.description}</span>
          <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 group-hover:gap-2.5 transition-[gap]">
            Enter {ws.label} <ArrowRight className="h-4 w-4" />
          </span>
        </span>
      </div>
    </button>
  );
};

export default function WorkspaceChooser() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { permissions, loading } = usePermissions();
  const workspaces = deriveWorkspaces(user, permissions);

  useEffect(() => {
    if (!loading && workspaces.length < 2 && workspaces[0]) {
      navigate(workspaces[0].home, { replace: true });
    }
  }, [loading, workspaces, navigate]);

  if (!user) return <Navigate to="/login" replace />;

  const stored = getActiveWorkspace();
  const alreadyChosen = workspaces.find((w) => w.id === stored);
  if (!loading && alreadyChosen) return <Navigate to={alreadyChosen.home} replace />;

  const choose = (ws) => {
    setActiveWorkspace(ws.id);
    navigate(ws.home, { replace: true });
  };

  if (loading || workspaces.length < 2) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="workspace-chooser-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-blue-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="workspace-chooser">
      <aside className="hidden lg:flex w-[38%] max-w-[520px] flex-col justify-between bg-[#0F172A] px-12 py-10 text-white">
        <TitleFlowLogo variant="darkBright" style={{ height: '34px', width: 'auto' }} />
        <div>
          <p className="text-sm text-slate-400">Welcome back,</p>
          <h1 className="mt-1 text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="workspace-chooser-name">{user.full_name || user.email}</h1>
          <p className="mt-6 max-w-sm text-base text-slate-300 leading-relaxed">
            You have access to multiple workspaces. Please choose where you want to continue.
          </p>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Your session is secure</div>
          <p className="mt-1 text-xs text-slate-400 leading-snug">All workspaces use the same login and permissions. Switching never starts a new session.</p>
        </div>
      </aside>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-xl">
          <h2 className="text-lg md:text-2xl font-semibold text-slate-900">Choose a workspace to continue</h2>
          <p className="mt-1 text-sm text-slate-500">You can switch workspaces anytime from the top right corner.</p>
          <div className="mt-8 space-y-4">
            {workspaces.map((ws, i) => <WorkspaceCard key={ws.id} ws={ws} index={i} onChoose={choose} />)}
          </div>
          <div className="mt-8 flex items-start gap-2 rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3 text-xs text-slate-600">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <span><span className="font-semibold text-slate-700">Tip:</span> your permissions determine what you can do in each workspace. The workspace itself never grants access.</span>
          </div>
        </div>
      </main>
    </div>
  );
}
